import { useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/ui/card";
import { Check, Sparkles, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { ALWAYS_INCLUDED, formatFeature } from "../lib/billing";
import { API } from "@/lib/api";
import { doorverwijzing } from "../lib/instellingenadres";
import { useTaal } from "@/contexts/TaalContext";
import { dagVanSeconden } from "@/lib/datum";


/**
 * G1 (Frank, 18 oktober 2026): het abonnement staat in Instellingen, op het
 * tabblad Abonnement, samen met de winkel. Dit blok is de inhoud; de pagina
 * hieronder zet er alleen de eigen kop omheen.
 */
export function AbonnementBlok({ onUpgrade }) {
  const { t, taal } = useTaal();
  const { user } = useAuth();
  const [license, setLicense] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [deployment, setDeployment] = useState("desktop");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/license/status`).catch(() => ({ data: null })),
      axios.get(`${API}/store/tiers`).catch(() => ({ data: { tiers: [] } })),
      axios.get(`${API}/billing/features`).catch(() => ({ data: {} })),
    ]).then(([licRes, tiersRes, legacyRes]) => {
      setLicense(licRes.data);
      setTiers(tiersRes.data.tiers || []);
      setDeployment(legacyRes.data.deployment || "desktop");
    }).finally(() => setLoading(false));
  }, []);

  const isDesktop = deployment === "desktop";
  const currentTier = tiers.find(t => t.key === license?.tier);

  const handleManageBilling = async () => {
    if (!user?.stripe_customer_id) {
      toast.info(t("Nog geen betaalaccount; neem eerst een betaald abonnement"));
      return;
    }
    setActionLoading(true);
    try {
      const resp = await axios.post(`${API}/store/portal`, null, { params: { customer_id: user.stripe_customer_id } });
      window.open(resp.data.url, "_blank");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Klantportaal openen is mislukt"));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <p className="py-6 text-center text-muted-foreground">{t("Laden...")}</p>;

  return (
    <div className="space-y-6" data-testid="abonnement-blok">
      {isDesktop && (
        <div className="px-3 py-2 rounded bg-muted/50 text-sm text-muted-foreground">
          {t("Desktopmodus: de licentie wordt op dit apparaat gecontroleerd en er gaat niets naar een server. Stripe regelt alleen de betaling; opwaarderen zet de functies van het abonnement aan op dit apparaat.")}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[hsl(var(--accent))]" />
            {t("{naam}-abonnement", { naam: license?.tier_label || "Free" })}
          </CardTitle>
          <CardDescription>
            {currentTier?.price_eur > 0 ? t("€{prijs} per maand", { prijs: currentTier.price_eur }) : t("Geen kosten")}
            {license?.expires_at > 0 && !license?.expired && (
              <> · {t("verlengt op {datum}", { datum: dagVanSeconden(license.expires_at, taal) })}</>
            )}
            {license?.expired && <span className="text-destructive"> {t("· verlopen")}</span>}
          </CardDescription>
        </CardHeader>
        <CardFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={handleManageBilling} disabled={actionLoading}>
            {t("Abonnement beheren")} <ExternalLink className="h-3 w-3 ml-1" />
          </Button>
          <Button onClick={() => onUpgrade?.()} data-testid="abonnement-opwaarderen">
            {license?.tier === "teams" ? t("Abonnementen bekijken") : t("Abonnement opwaarderen")}
          </Button>
        </CardFooter>
      </Card>

      <div>
        <h2 className="text-sm font-medium text-muted-foreground mb-3 uppercase tracking-wider">
          {t("Wat er bij {naam} hoort", { naam: license?.tier_label || "Free" })}
        </h2>
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              {(currentTier?.features || ALWAYS_INCLUDED).map(f => (
                <div key={f} className="flex items-center gap-2">
                  <Check className="h-3.5 w-3.5 text-ok shrink-0" />
                  <span>{t(formatFeature(f))}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// G4: de oude route verwijst door naar Instellingen, met de melding van Stripe erbij.
export default function BillingPage() {
  const location = useLocation();
  return <Navigate to={doorverwijzing("/billing", location.search)} replace />;
}
